import { useState } from 'react';
import { palette } from '../../styles/tokens';
import { Card } from '../common/Card';
import type { Business } from '../../types';

interface Props {
  vocabulary: Record<string, string>;
  onSave: (updates: Partial<Business>) => Promise<boolean>;
}

const TERMS = [
  { key: 'employee', defaultLabel: 'Employee' },
  { key: 'booking', defaultLabel: 'Booking' },
  { key: 'shift', defaultLabel: 'Shift' },
  { key: 'job', defaultLabel: 'Job' },
];

const ROW: React.CSSProperties = { display: 'flex', alignItems: 'center', padding: '8px 0', borderBottom: `1px solid ${palette.g100}` };
const INPUT: React.CSSProperties = {
  padding: '5px 10px', borderRadius: 6, border: `1px solid ${palette.g200}`,
  fontSize: 13.5, fontFamily: 'inherit', color: palette.g800, width: 180,
};

export function VocabularySection({ vocabulary, onSave }: Props) {
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState<Record<string, string>>({});

  const startEdit = () => {
    const f: Record<string, string> = {};
    for (const t of TERMS) f[t.key] = vocabulary[t.key] ?? '';
    setForm(f);
    setEditing(true);
  };

  const save = async () => {
    const cleaned: Record<string, string> = {};
    for (const t of TERMS) {
      if (form[t.key] && form[t.key] !== t.defaultLabel) cleaned[t.key] = form[t.key];
    }
    await onSave({ vocabulary: cleaned } as Partial<Business>);
    setEditing(false);
  };

  if (!editing) {
    return (
      <Card title="Custom Vocabulary" actionLabel="Edit" onAction={startEdit}>
        {TERMS.map((t, i) => {
          const custom = vocabulary[t.key];
          return (
            <div key={t.key} style={{ ...ROW, borderBottom: i === TERMS.length - 1 ? 'none' : ROW.borderBottom }}>
              <div style={{ fontSize: 13, color: palette.g500, width: 120 }}>"{t.defaultLabel}"</div>
              <div style={{ fontSize: 13, color: palette.g400, margin: '0 8px' }}>→</div>
              <div style={{ fontSize: 13.5, fontWeight: 500, color: custom ? palette.g800 : palette.g400 }}>
                {custom || t.defaultLabel}
                {!custom && <span style={{ fontSize: 11, color: palette.g300, marginLeft: 8 }}>(default)</span>}
              </div>
            </div>
          );
        })}
      </Card>
    );
  }

  return (
    <Card title="Custom Vocabulary">
      {TERMS.map((t, i) => (
        <div key={t.key} style={{ ...ROW, borderBottom: i === TERMS.length - 1 ? 'none' : ROW.borderBottom, gap: 12 }}>
          <div style={{ fontSize: 13, color: palette.g500, width: 120 }}>"{t.defaultLabel}"</div>
          <div style={{ fontSize: 13, color: palette.g400 }}>→</div>
          <input style={INPUT} value={form[t.key]} onChange={e => setForm(f => ({ ...f, [t.key]: e.target.value }))} placeholder={t.defaultLabel} />
        </div>
      ))}
      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
        <button onClick={() => setEditing(false)} style={{ padding: '7px 16px', borderRadius: 6, border: 'none', background: 'transparent', fontSize: 13, fontWeight: 500, color: palette.g500, cursor: 'pointer', fontFamily: 'inherit' }}>Cancel</button>
        <button onClick={save} style={{ padding: '7px 20px', borderRadius: 6, border: 'none', background: palette.accent, color: palette.white, fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>Save</button>
      </div>
    </Card>
  );
}
